import React from "react";

interface TimelineEvent {
  title: string;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  events?: string | TimelineEvent[];
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultEvents: TimelineEvent[] = [
  {
    title: "",
    description: "",
    icon: "1️⃣",
  },
  {
    title: "",
    description: "",
    icon: "2️⃣",
  },
  {
    title: "",
    description: "",
    icon: "3️⃣",
  },
  {
    title: "",
    description: "",
    icon: "4️⃣",
  },
];

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const TimelineSlide2 = {
  id: "timeline-slide2",
  name: "Timeline Style 2",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "events",
      label: "Events",
      type: "text",
      defaultValue: JSON.stringify(defaultEvents),
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #166534 0%, #15803d 50%, #16a34a 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      events = JSON.stringify(defaultEvents),
      bgColor = "linear-gradient(135deg, #166534 0%, #15803d 50%, #16a34a 100%)",
      onEdit = () => {},
    } = content;

    let parsedEvents = defaultEvents;
    try {
      if (typeof events === "string") {
        parsedEvents = JSON.parse(events);
      } else if (Array.isArray(events)) {
        parsedEvents = events;
      }
    } catch (error) {
      console.warn("Error parsing events:", error);
    }

    const handleEventEdit = (index: number, field: string, value: string) => {
      const updatedEvents = [...parsedEvents];
      updatedEvents[index] = {
        ...updatedEvents[index],
        [field]: value,
      };
      onEdit({ ...content, events: JSON.stringify(updatedEvents) });
    };

    return (
      <div className="w-full h-full relative overflow-hidden text-white">
        {/* Dynamic Background */}
        <div
          className="absolute inset-0 transition-all duration-1000"
          style={{ background: bgColor }}
        >
          {/* Animated Background Patterns */}
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute w-full h-full"
              style={{
                backgroundImage: `
                  radial-gradient(circle at 20% 20%, rgba(255,255,255,0.5) 0%, transparent 25%),
                  radial-gradient(circle at 80% 80%, rgba(255,255,255,0.5) 0%, transparent 25%),
                  radial-gradient(circle at 50% 50%, rgba(255,255,255,0.5) 0%, transparent 35%)
                `,
              }}
            />
          </div>

          {/* Animated Lines */}
          <div className="absolute inset-0 opacity-20">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="absolute w-full h-1 bg-white/20 animate-pulse"
                style={{
                  top: `${30 + i * 30}%`,
                  transform: `rotate(${i * 60}deg)`,
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full p-8">
          {/* Header Section */}
          <div className="text-center mb-12">
            <EditableDiv
              text={title}
              onEdit={(value) => onEdit({ ...content, title: value })}
              className="text-4xl font-bold mb-3 bg-clip-text text-transparent bg-gradient-to-r 
                        from-white via-green-100 to-white animate-text-shine"
            />
            <EditableDiv
              text={subtitle}
              onEdit={(value) => onEdit({ ...content, subtitle: value })}
              className="text-xl text-green-100/90 font-light"
            />
          </div>

          {/* Timeline Container */}
          <div className="flex-1 flex flex-col justify-center px-4">
            <div className="relative">
              {/* Center Line with Gradient */}
              <div
                className="absolute left-0 right-0 top-1/2 h-1 bg-gradient-to-r 
                            from-transparent via-white/30 to-transparent transform -translate-y-1/2"
              />

              {/* Events */}
              <div className="flex justify-between items-center relative">
                {parsedEvents.map((event, idx) => (
                  <div
                    key={idx}
                    className="group relative px-4"
                    style={{ width: `${100 / parsedEvents.length}%` }}
                  >
                    {/* Event Card */}
                    <div
                      className={`relative bg-white/5 backdrop-blur-md rounded-2xl p-6 mb-8
                                transform transition-all duration-500 
                                ${idx % 2 === 0 ? "hover:-translate-y-2" : "hover:translate-y-2"}
                                hover:scale-105 border border-white/10 hover:border-white/30
                                hover:shadow-[0_0_30px_rgba(255,255,255,0.1)]`}
                      style={{
                        background: `linear-gradient(165deg, 
                          rgba(255,255,255,0.1) 0%, 
                          rgba(255,255,255,0.05) 40%, 
                          transparent 100%
                        )`,
                      }}
                    >
                      {/* Icon Container */}
                      <div className="relative mb-4">
                        <div
                          className="absolute -inset-2 bg-white/20 rounded-full blur-md opacity-0 
                                    group-hover:opacity-100 transition-opacity duration-500"
                        />
                        <EditableDiv
                          text={event.icon}
                          onEdit={(value) =>
                            handleEventEdit(idx, "icon", value)
                          }
                          className="text-3xl transform transition-all duration-500 
                                    group-hover:scale-110 group-hover:rotate-12"
                        />
                      </div>

                      {/* Title */}
                      <EditableDiv
                        text={event.title}
                        onEdit={(value) => handleEventEdit(idx, "title", value)}
                        className="text-lg font-bold mb-2 bg-gradient-to-r from-white to-green-100 
                                bg-clip-text text-transparent"
                      />

                      {/* Description */}
                      <EditableDiv
                        text={event.description}
                        onEdit={(value) =>
                          handleEventEdit(idx, "description", value)
                        }
                        className="text-sm text-white/80 leading-relaxed
                                transform transition-all duration-300 group-hover:text-white"
                      />

                      {/* Bottom Highlight */}
                      <div className="absolute bottom-0 left-0 w-full h-1 overflow-hidden">
                        <div
                          className="w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent
                                    transform -translate-x-full group-hover:translate-x-full transition-transform 
                                    duration-1000"
                        />
                      </div>

                      {/* Connecting Line */}
                      <div
                        className="absolute bottom-0 left-1/2 w-0.5 h-8 
                                  bg-gradient-to-b from-white/30 to-transparent
                                  transform translate-y-full -translate-x-1/2"
                      />
                    </div>

                    {/* Timeline Node */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-[0.5px]">
                      <div className="relative">
                        <div
                          className="w-4 h-4 rounded-full bg-white/30 backdrop-blur-sm
                                    transform transition-all duration-500 group-hover:scale-150
                                    border border-white/50 group-hover:border-white/80"
                        />
                        <div
                          className="absolute inset-0 rounded-full animate-ping 
                                    opacity-75 bg-white/20"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
};

export default TimelineSlide2;
